import { ElementProps } from '@/components/sections/builder/types';
import { useBuilderStore } from '@/stores/builder';

import { ElementDataType } from '@/components/sections/builder/elements/body/multicolumn/type';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import { Input } from '@/components/ui/input';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import InputLink from '@/components/sections/builder/settings/input-link';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import ColorPicker from '@/components/commons/color-picker';
import { ColorResult } from 'react-color';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

const IMAGE_WIDTHS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'one_third_width', label: 'One-third width of column' },
  { value: 'half_width', label: 'Half width of column' },
  { value: 'full_width', label: 'Full width of column' },
];

const IMAGE_POSITION = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Top Center', value: 'top' },
  { label: 'Top Left', value: 'top-left' },
  { label: 'Top Right', value: 'top-right' },
  { label: 'Bottom Center', value: 'bottom' },
  { label: 'Bottom Left', value: 'bottom-left' },
  { label: 'Bottom Right', value: 'bottom-right' },
];

const IMAGE_RATIOS = [
  { value: 'adapt', label: 'Adapt to image' },
  { value: 'potrait', label: 'Potrait' },
  { value: 'square', label: 'Square' },
  { value: 'circle', label: 'Circle' },
];

const FONT_SIZE_OPTIONS = [
  { label: 'Extra Small', value: 'xs' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'base' },
  { label: 'Large', value: 'lg' },
  { label: 'Extra Large', value: 'xl' },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const content = element.data as ElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        secondaryBgColor: {
          ...content.secondaryBgColor,
          color: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
        },
      });
    } else {
      builder.setElementData(element.id, {
        secondaryBgColor: {
          ...content.secondaryBgColor,
          color: color.hex,
        },
      });
    }
  };

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Heading Settings</h1>
      <div className='mb-1 text-xs'>Heading</div>
      <TextEditorMinimal
        value={content.heading as string}
        onChange={(val) => {
          builder.setElementData(element.id, {
            heading: val,
          });
        }}
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-1 mt-4 text-xs'>Description</div>
      <TextEditorMinimal
        onChange={(value) => {
          builder.setElementData(element.id, {
            description: value,
          });
        }}
        value={content.description || ''}
        placeholder='Description'
        className='mb-4 w-full'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Heading Size</h4>
        <InputSelect
          defaultValue={content.headingSize}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { headingSize: v })}
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Description style</h4>
        <InputSelect
          defaultValue={content.textStyle}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              textStyle: v,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Heading Alignment</h4>
        <InputSelect
          defaultValue={content.titleAlign}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { titleAlign: v })}
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-4 text-sm font-bold'>Content Settings</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Dekstop heading font size</h4>
        <InputSelect
          defaultValue={content.dekstopContentHeadingFontSize}
          options={FONT_SIZE_OPTIONS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setElementData(element.id, {
              dekstopContentHeadingFontSize: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Dekstop description font size</h4>
        <InputSelect
          defaultValue={content.dekstopContentDescriptionFontSize}
          options={FONT_SIZE_OPTIONS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setElementData(element.id, {
              dekstopContentDescriptionFontSize: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Mobile heading font size</h4>
        <InputSelect
          defaultValue={content.mobileContentHeadingFontSize}
          options={FONT_SIZE_OPTIONS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setElementData(element.id, {
              mobileContentHeadingFontSize: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Mobile description font size</h4>
        <InputSelect
          defaultValue={content.mobileContentDescriptionFontSize}
          options={FONT_SIZE_OPTIONS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setElementData(element.id, {
              mobileContentDescriptionFontSize: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Button column style </h4>
        <InputSelect
          defaultValue={content.buttonStyleColumn ?? 'none'}
          options={[
            { label: 'Color', value: 'color' },
            { label: 'None', value: 'none' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              buttonStyleColumn: v,
            })
          }
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-4 text-sm font-bold'>Image Settings</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Image Width</h4>
        <InputSelect
          defaultValue={content.imageWidth}
          options={IMAGE_WIDTHS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setElementData(element.id, {
              imageWidth: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Image Ratio</h4>
        <InputSelect
          defaultValue={content.imageRatio}
          options={IMAGE_RATIOS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setElementData(element.id, {
              imageRatio: val,
            })
          }
        />
      </div>

      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Number of columns on desktop</h4>
        <InputSlider
          value={content.numberOfColumns}
          min={1}
          max={5}
          onChange={(val) =>
            builder.setElementData(element.id, {
              numberOfColumns: val,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Number of columns on mobile</h4>
        <InputSelect
          defaultValue={content.totalColMobile ?? 'one_column'}
          options={[
            { label: '1 column', value: 'one_column' },
            { label: '2 columns', value: 'two_columns' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              totalColMobile: v,
            })
          }
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-4 text-sm font-bold'>Button Settings</h1>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Label </label>
        <Input
          onChange={(e) =>
            builder.setElementData(element.id, {
              buttonLabel: e.target.value,
            })
          }
          value={content.buttonLabel}
          name='buttonLabel'
          type='text'
          size={100}
        />
      </div>
      <div className='mb-2 flex flex-col gap-2'>
        <label className='text-xs'>Link </label>
        <InputLink
          name='url_path'
          placeholder='Search or enter a link'
          onChange={(value) => {
            builder.setElementData(element.id, {
              buttonLink: value,
            });
          }}
          value={content.buttonLink as string}
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-4 text-sm font-bold'>Style</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Content Position</h4>
        <InputSelect
          defaultValue={content.imagePosition}
          options={IMAGE_POSITION.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(val) =>
            builder.setElementData(element.id, {
              imagePosition: val,
            })
          }
        />
      </div>
      {content.imagePosition === 'left' ||
        (content.imagePosition === 'right' && (
          <Badge variant='outline' className='mb-2 p-1 px-2'>
            For Content Left & Right, we recommend ≤4 columns.
            <br />
            With 5 columns, it automatically switches to &quot;Top&quot; on narrow screens.
          </Badge>
        ))}
      <div className='mb-4 flex items-center gap-2'>
        <label>Enable swipe on mobile</label>
        <Switch
          className='ml-auto'
          checked={content.enableSwipeMobile}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableSwipeMobile: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4>Secondary Background</h4>
        <InputSelect
          defaultValue={content.secondBackground ?? 'false'}
          options={[
            { label: 'None', value: 'false' },
            { label: 'column background', value: 'true' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { secondBackground: v })}
        />
      </div>
      {content.secondBackground !== 'false' && (
        <div className='mt-4 flex flex-col gap-4'>
          <div>
            <span className='mb-2 flex text-xs'>Secondary background color</span>
            <ColorPicker
              color={content.secondaryBgColor?.color}
              inputColor={content.secondaryBgColor?.color}
              onChangeColorComplete={(color) => onChangeColor(color)}
              onChangeInputColor={(e) => {
                builder.setElementData(element.id, {
                  secondaryBgColor: {
                    ...content.secondaryBgColor,
                    color: e.target.value,
                  },
                });
              }}
              onChangePercentage={(value) => {
                builder.setElementData(element.id, {
                  secondaryBgColor: {
                    ...content.secondaryBgColor,
                    opacity: value,
                  },
                });
              }}
              isShowPercentage={true}
            />
          </div>
        </div>
      )}
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4 className='text-xs'>Button Radius</h4>
        <InputSlider
          value={content.buttonRadius}
          min={0}
          max={32}
          onChange={(val) =>
            builder.setElementData(element.id, {
              buttonRadius: val,
            })
          }
        />
      </div>
      <div className='mb-1 text-xs md:mt-4'>Color Scheme</div>
      <div className=''>
        <SchemeSetting
          value={content.colorScheme}
          onChange={(theme) => {
            builder.setElementData(element.id, {
              colorScheme: theme,
            });
          }}
        />
      </div>
      <PaddingSetting
        value={content.padding}
        onChange={(padding) => {
          builder.setElementData(element.id, {
            padding: padding,
          });
        }}
      />
    </div>
  );
}
